import {
  createSlice,
  createEntityAdapter,
  PayloadAction,
} from '@reduxjs/toolkit'
import {
  ReconciliationPeriod,
  PaymentReconciliationRecord,
  PaymentReconciliationStatus,
} from 'enrollment-platform/pages/finance/api/dto/get-payment-reconciliation-activities.dto'
import { RootState } from 'store/types'

export interface PaymentReconciliationEntity {
  id: string // paymentReconciliationId
  benefitId: string
  benefitVersion: number
  periodStartDate: string
  surecoExpected: number | null
  carrierExpected: number | null
  customerCostExpected: number | null
  totalAmount: number
  reconciliationStatus: PaymentReconciliationStatus
  activities: ReconciliationPeriod['activities']
  employeeName?: string
  employeePublicKey?: string
}

const paymentReconciliationAdapter =
  createEntityAdapter<PaymentReconciliationEntity>()

interface PaymentReconciliationState {
  reconciliationPeriods: ReturnType<
    typeof paymentReconciliationAdapter.getInitialState
  >
  loading: boolean
  error: string | null
}

const initialState: PaymentReconciliationState = {
  reconciliationPeriods: paymentReconciliationAdapter.getInitialState(),
  loading: false,
  error: null,
}

const transformReconciliationData = (
  records: PaymentReconciliationRecord[]
): PaymentReconciliationEntity[] => {
  return records.flatMap((record) =>
    Object.entries(record.reconciliationData)
      .filter(([, periodData]) => periodData)
      .map(([monthDate, periodData]) => ({
        id: periodData.id.toString(),
        benefitId: record.benefitId,
        benefitVersion: record.version ?? 0,
        periodStartDate: periodData.periodStartDate || monthDate,
        surecoExpected: periodData.surecoExpected
          ? Number(periodData.surecoExpected)
          : null,
        carrierExpected: periodData.carrierExpected
          ? Number(periodData.carrierExpected)
          : null,
        customerCostExpected: periodData.customerCostExpected || null,
        totalAmount: periodData.totalAmount || 0,
        reconciliationStatus: periodData.reconciliationStatus || 'paid',
        activities: periodData.activities || [],
        employeeName: record.employee?.name,
        employeePublicKey: record.employee?.publicKey,
      }))
  )
}

const paymentReconciliationSlice = createSlice({
  name: 'paymentReconciliation',
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload
    },
    setReconciliationData: (
      state,
      action: PayloadAction<PaymentReconciliationRecord[]>
    ) => {
      const entities = transformReconciliationData(action.payload)
      paymentReconciliationAdapter.setAll(state.reconciliationPeriods, entities)
    },
    updateReconciliationPeriod: (
      state,
      action: PayloadAction<
        Partial<PaymentReconciliationEntity> & { id: string }
      >
    ) => {
      paymentReconciliationAdapter.updateOne(state.reconciliationPeriods, {
        id: action.payload.id,
        changes: action.payload,
      })
    },
    clearReconciliationData: (state) => {
      paymentReconciliationAdapter.removeAll(state.reconciliationPeriods)
    },
  },
})

export const {
  setLoading,
  setError,
  setReconciliationData,
  updateReconciliationPeriod,
  clearReconciliationData,
} = paymentReconciliationSlice.actions

export const {
  selectAll: selectAllReconciliationPeriods,
  selectById: selectReconciliationPeriodById,
  selectIds: selectReconciliationPeriodIds,
  selectEntities: selectReconciliationPeriodEntities,
  selectTotal: selectReconciliationPeriodsTotal,
} = paymentReconciliationAdapter.getSelectors(
  (state: RootState) => state.paymentReconciliation.reconciliationPeriods
)

export const selectPaymentReconciliationLoading = (state: RootState) =>
  state.paymentReconciliation.loading

export const selectPaymentReconciliationError = (state: RootState) =>
  state.paymentReconciliation.error

export const selectReconciliationPeriodsByBenefitId =
  (benefitId: string) => (state: RootState) =>
    selectAllReconciliationPeriods(state).filter(
      (period) => period.benefitId === benefitId
    )

export default paymentReconciliationSlice.reducer
